import type { JSX } from 'react';
import { useState } from 'react';
import {
  acceptClinicianInvite,
  MIN_PASSWORD_LENGTH,
  PublicError,
  takeTokenFromUrl,
} from '../../data/public';
import { signIn } from '../../data/session';
import { FormError, NewPassword, passwordProblem, PublicCard } from './PublicCard';

/**
 * `/accept?token=…` — a clinician sets a password and joins the organisation.
 *
 * No email field: the address is the invitation's, and the API takes it from
 * there rather than from the request, so a leaked link cannot be pointed at
 * somebody else's mailbox. The response says which address it was, which is
 * what lets this page sign the new clinician straight in.
 */
export function Accept(): JSX.Element {
  const [token] = useState(takeTokenFromUrl);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState<string | null>(null);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (token === null) return;
    const problem = passwordProblem(password, confirm, MIN_PASSWORD_LENGTH);
    if (problem !== null) {
      setError(problem);
      return;
    }

    setBusy(true);
    setError(null);
    let email: string;
    try {
      ({
        member: { email },
      } = await acceptClinicianInvite({ token, password }));
    } catch (err) {
      setError(err instanceof PublicError ? err.message : 'Something went wrong. Try again.');
      setBusy(false);
      return;
    }

    try {
      await signIn(email, password);
      window.location.assign('/');
    } catch {
      // The account exists; only the automatic sign-in failed. Say so, rather
      // than an error that reads as though accepting did not work.
      setJoined(email);
    }
  }

  if (joined !== null) {
    return (
      <PublicCard subtitle="You have joined">
        <p className="invite__preamble">
          Your account is set up as <strong>{joined}</strong>. Sign in with the password you just
          chose.
        </p>
        <a className="primary signin__submit" href="/">
          Sign in
        </a>
      </PublicCard>
    );
  }

  if (token === null) {
    return (
      <PublicCard subtitle="Accept your invitation">
        <p className="invite__preamble">
          This link is incomplete. Open the link from your invitation email again, or copy the
          whole of it into the address bar.
        </p>
      </PublicCard>
    );
  }

  return (
    <PublicCard subtitle="Accept your invitation">
      <form onSubmit={submit}>
        <FormError
          message={
            error === null
              ? null
              : `${error} If you have already accepted this invitation, sign in instead; otherwise ask whoever invited you to send a new one.`
          }
        />
        <p className="invite__preamble">
          Choose a password to finish joining. You will sign in with the email address the
          invitation was sent to.
        </p>
        <NewPassword
          password={password}
          confirm={confirm}
          onPassword={setPassword}
          onConfirm={setConfirm}
          minLength={MIN_PASSWORD_LENGTH}
        />
        <button type="submit" className="primary signin__submit" disabled={busy}>
          {busy ? 'Setting up…' : 'Join'}
        </button>
      </form>
    </PublicCard>
  );
}
